import Spin from 'antd/lib/spin';
import { GlobalState } from 'core/config';
import { languageService } from 'core/services/LanguageService';
import { AppUser } from 'models/AppUser';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { RouteConfig, RouteConfigComponentProps } from 'react-router-config';
import { useHistory, withRouter } from 'react-router-dom';
import { setGlobal } from 'reactn';
import authenticationService from 'services/AuthenticationService';
import signalRService from 'services/SignalRService';
import { SignalRContext } from './AppContext';
import ErrorBoundary from './ErrorBoundary';
import AppGuard from './AppGuard';
import { LOGIN_ROUTE, PPF_ROUTE } from 'config/route-consts';

export interface AppProps extends RouteConfigComponentProps {
  routes: RouteConfig[];
}

function App(props: AppProps) {
  const [translate] = useTranslation();
  const { routes } = props;
  const [loading, setLoading] = React.useState(true);
  const history = useHistory();
  languageService.useLanguage();

  const handleRefreshToken = React.useCallback(
    (isSetLoading?: boolean) => {
      authenticationService
        .refreshToken()
        .then((expiredTime: number) => {
          if (expiredTime) {
            const refreshTime =
              Math.round(expiredTime) > 20
                ? (Math.round(expiredTime) - 20) * 1000
                : 0;
            setTimeout(() => {
              handleRefreshToken(true);
            }, refreshTime);
          }
          if (!isSetLoading) {
            const user: AppUser = JSON.parse(localStorage.getItem('profile'));
            setGlobal<GlobalState>({
              user,
              language: user.language?.code || 'vi',
            });
            const landingPage = user.appUserSiteMappings.filter(current => {
              return (
                current.id === 100 || current.site?.code === '/landing-page'
              );
            })[0];
            if (landingPage) {
              document.title = landingPage.site?.name;
            }

            const projects = user.appUserSiteMappings.filter(
              item => item.site.code === '/portal/',
            );
            if (!projects.length) return (window.location.href = PPF_ROUTE);
            if (!projects[0].enabled) return (window.location.href = PPF_ROUTE);
            setLoading(false);
          }
        })
        .catch(() => {
          history.push(LOGIN_ROUTE);
        });
    },
    [history],
  );

  React.useEffect(() => {
    authenticationService
      .checkAuth()
      .then((user: AppUser) => {
        if (user) {
          setGlobal<GlobalState>({
            user,
            language: user.language?.code || 'vi',
          });

          localStorage.setItem('I18N_LANGUAGE', user?.language?.code || 'en');
          const refreshTime =
            Math.round(user.tokenExpiredInSeconds) > 20
              ? (Math.round(user.tokenExpiredInSeconds) - 20) * 1000
              : 0;
          setTimeout(() => {
            handleRefreshToken();
          }, refreshTime);
          const profileValue = JSON.stringify(user);
          localStorage.setItem('profile', profileValue);
          const landingPage = user.appUserSiteMappings.filter(current => {
            return current.id === 100 || current.site?.code === '/landing-page';
          })[0];
          if (landingPage) {
            document.title = landingPage.site?.name;
          }
          setLoading(false);
        }
      })
      .catch(() => {
        handleRefreshToken();
      });
  }, [handleRefreshToken]);

  return (
    <>
      {loading ? (
        <div id="app">
          <Spin tip={translate('pages.checking.authority')} />
        </div>
      ) : (
        <ErrorBoundary>
          <SignalRContext.Provider value={signalRService}>
            <AppGuard routes={routes} />
          </SignalRContext.Provider>
        </ErrorBoundary>
      )}
    </>
  );
}

export default withRouter(App);
